import type { ReactNode } from "react";
import { cn, FOCUS_RING } from "./cn";
import { Spinner } from "./Spinner";

export const TABS_VARIANTS = ["underline", "pills"] as const;

export type TabsVariant = (typeof TABS_VARIANTS)[number];

const TAB_LIST_CLASSES = {
  underline: "border-b border-line",
  pills: "rounded-lg bg-surface p-1",
} as const satisfies Record<TabsVariant, string>;

const TAB_GEOMETRY_CLASSES = {
  underline: "border-b-2 px-4 pb-2.5 pt-2 -mb-px",
  pills: "rounded-md px-4 py-1.5",
} as const satisfies Record<TabsVariant, string>;

const TAB_STATE_CLASSES = {
  underline: {
    active: "border-primary-500 text-primary-500",
    inactive: "border-transparent text-content-secondary hover:border-line-soft hover:text-content",
  },
  pills: {
    active: "bg-card text-content shadow-sm",
    inactive: "text-content-secondary hover:text-content",
  },
} as const satisfies Record<TabsVariant, Record<"active" | "inactive", string>>;

const TAB_COUNT_CLASSES = {
  active: "bg-primary-500/15 text-primary-500",
  inactive: "bg-subtle text-content-muted",
} as const satisfies Record<"active" | "inactive", string>;

export interface TabItem {
  id: string;
  label: ReactNode;
  icon?: string;
  count?: number;
  countLoading?: boolean;
  disabled?: boolean;
}

export interface TabsProps {
  items: TabItem[];
  activeId: string;
  onChange: (id: string) => void;
  variant?: TabsVariant;
  className?: string;
}

export function Tabs({
  items,
  activeId,
  onChange,
  variant = "underline",
  className,
}: TabsProps) {
  return (
    <div
      role="tablist"
      className={cn(
        "flex flex-wrap items-center gap-2",
        TAB_LIST_CLASSES[variant],
        className,
      )}
    >
      {items.map((item) => {
        const state = item.id === activeId ? "active" : "inactive";
        return (
          <button
            key={item.id}
            role="tab"
            type="button"
            aria-selected={state === "active"}
            disabled={item.disabled}
            onClick={() => onChange(item.id)}
            className={cn(
              "inline-flex shrink-0 items-center gap-2 whitespace-nowrap text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed",
              FOCUS_RING,
              TAB_GEOMETRY_CLASSES[variant],
              TAB_STATE_CLASSES[variant][state],
            )}
          >
            {item.icon ? <i className={item.icon} /> : null}
            {item.label}
            {item.count === undefined && !item.countLoading ? null : (
              <span
                className={cn(
                  "inline-flex h-4 min-w-[1.125rem] shrink-0 items-center justify-center rounded-full px-1.5 text-[10px] font-semibold leading-none",
                  TAB_COUNT_CLASSES[state],
                )}
              >
                {item.countLoading ? <Spinner size="xs" /> : item.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
